import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '@/utils/api';

import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { UserAvatar } from '@/components/UserAvatar';
import { MobileMenuTrigger } from '@/components/Layout';
import { CRMListSkeleton } from '@/components/Skeleton';
import PullToRefresh from '@/components/PullToRefresh';
import { hapticLight } from '@/utils/haptics';
import { 
  Plus, Search, ChevronRight, Flame, ThermometerSun, 
  Snowflake, Trophy, XCircle, Calendar, Clock, Eye, RefreshCw,
  Building2, SlidersHorizontal, Bell, AlertTriangle, FileText,
  Phone, CheckCircle, MessageSquare, Bookmark, Upload, Download, Loader2,
  User, Users, Home, ArrowLeft, MapPin, CalendarDays, Info, RotateCcw
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

// First Visit Filter Presets
const FIRST_VISIT_PRESETS = [
  { value: 'any', label: 'Any time' },
  { value: 'today', label: 'Today' },
  { value: 'last_7_days', label: 'Last 7 days' },
  { value: 'last_30_days', label: 'Last 30 days' },
  { value: 'this_month', label: 'This month' },
  { value: 'last_month', label: 'Last month' },
  { value: 'custom', label: 'Custom range' }
];

// Scheduled Visit Filter Presets
const SCHEDULED_VISIT_PRESETS = [
  { value: 'any', label: 'Any' },
  { value: 'today', label: 'Today' },
  { value: 'tomorrow', label: 'Tomorrow' },
  { value: 'next_3_days', label: 'Next 3 Days' },
  { value: 'this_week', label: 'This Week' },
  { value: 'custom', label: 'Custom Date' }
];

const CRM = ({ user }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [customers, setCustomers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addingCustomer, setAddingCustomer] = useState(false);
  const [loadError, setLoadError] = useState(null);
  
  // Bulk Upload State (Admin only)
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [csvContent, setCsvContent] = useState('');
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadResults, setUploadResults] = useState(null);
  const fileInputRef = useRef(null);
  
  const isAdmin = user?.role === 'Admin' || user?.role === 'super_admin' || user?.role === 'city_admin';
  
  // Check URL params for initial filter state
  const initialFollowupFilter = searchParams.get('filter') === 'followups_due' || searchParams.get('filter') === 'overdue';
  
  // Filters
  const [stageFilter, setStageFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [activityFilter, setActivityFilter] = useState('all');
  const [sortBy, setSortBy] = useState('next_followup');
  const [showFilters, setShowFilters] = useState(false);
  const [followupsDueToday, setFollowupsDueToday] = useState(initialFollowupFilter);
  
  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);
  
  // CA Filter (Admin only)
  const [caFilter, setCaFilter] = useState('all');
  const [caList, setCaList] = useState([]);
  
  // City Filter (Admin/Manager only)
  const [cityFilter, setCityFilter] = useState('all');
  const [cityList, setCityList] = useState([]);
  
  // First Visit Date Filter
  const [firstVisitFilter, setFirstVisitFilter] = useState('any');
  const [firstVisitCustomFrom, setFirstVisitCustomFrom] = useState('');
  const [firstVisitCustomTo, setFirstVisitCustomTo] = useState('');
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
  
  // Scheduled Visit Filter
  const [scheduledVisitFilter, setScheduledVisitFilter] = useState('any');
  const [scheduledVisitCustomFrom, setScheduledVisitCustomFrom] = useState('');
  const [scheduledVisitCustomTo, setScheduledVisitCustomTo] = useState('');
  const [showScheduledDatePicker, setShowScheduledDatePicker] = useState(false);

  // Retargeting Filter
  const [retargetingFilter, setRetargetingFilter] = useState(false);

  // Swipe state
  const [swipedCardId, setSwipedCardId] = useState(null);
  const touchStartX = useRef(0);

  // Fetch CA list for admin filter
  useEffect(() => {
    if (isAdmin) {
      fetchCAList();
      fetchCityList();
    }
  }, [isAdmin]);

  const fetchCAList = async () => {
    try {
      const response = await api.getUsers({ role: 'rep', limit: 500 });
      const users = response.data?.users || response.data || [];
      // Extract unique CAs from the list
      const cas = users.map(u => ({
        user_id: u.user_id,
        name: u.rep_name || u.full_name || u.email,
        city: u.city
      }));
      setCaList(cas);
    } catch (error) {
      console.error('Failed to fetch CA list:', error);
    }
  };

  const fetchCityList = async () => {
    try {
      const response = await api.getCRMCities();
      setCityList(response.data?.cities || []);
    } catch (error) {
      console.error('Failed to fetch city list:', error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [stageFilter, activityFilter, sortBy, followupsDueToday, caFilter, cityFilter, firstVisitFilter, firstVisitCustomFrom, firstVisitCustomTo, scheduledVisitFilter, scheduledVisitCustomFrom, scheduledVisitCustomTo, retargetingFilter, debouncedSearchQuery]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = { sort_by: sortBy };
      if (stageFilter !== 'all') params.stage = stageFilter;
      if (activityFilter !== 'all') params.last_activity_days = parseInt(activityFilter);
      if (followupsDueToday) params.followups_due_today = 'true';
      
      // First Visit Date Filter
      if (firstVisitFilter !== 'any') {
        if (firstVisitFilter === 'custom') {
          if (firstVisitCustomFrom) params.first_visit_from = firstVisitCustomFrom;
          if (firstVisitCustomTo) params.first_visit_to = firstVisitCustomTo;
        } else {
          params.first_visit_preset = firstVisitFilter;
        }
      }
      
      // Scheduled Visit Filter
      if (scheduledVisitFilter !== 'any') {
        if (scheduledVisitFilter === 'custom') {
          params.scheduled_visit_preset = 'custom';
          if (scheduledVisitCustomFrom) params.scheduled_visit_from = scheduledVisitCustomFrom;
          if (scheduledVisitCustomTo) params.scheduled_visit_to = scheduledVisitCustomTo;
        } else {
          params.scheduled_visit_preset = scheduledVisitFilter;
        }
        // Auto-sort by scheduled visit time when filter is active
        params.sort_by = 'scheduled_visit';
      }
      
      // Retargeting Filter
      if (retargetingFilter) {
        params.is_retargeting = 'true';
      }
      
      // Admin users see all customers by default
      if (isAdmin) {
        params.view_all = 'true';
        // Apply City filter if selected
        if (cityFilter && cityFilter !== 'all') {
          params.city = cityFilter;
        }
        // Apply CA filter if selected
        if (caFilter === 'unassigned') {
          params.unassigned = 'true';
        } else if (caFilter && caFilter !== 'all') {
          params.rep_user_id = caFilter;
        }
      }
      
      // Build stats params - SAME filters for consistency (minus stage, plus search)
      const statsParams = { ...params };
      delete statsParams.sort_by;  // Stats don't need sort
      delete statsParams.stage;    // Stats return all stage counts
      if (debouncedSearchQuery) {
        statsParams.search = debouncedSearchQuery;
        params.search = debouncedSearchQuery;  // Also pass to customers API
      }
      
      const [customersRes, statsRes] = await Promise.all([
        api.getCustomers(params),
        api.getCRMStats(statsParams)  // Pass same filters to stats API
      ]);
      // Handle both old array format and new paginated format
      const customersData = customersRes.data?.customers || customersRes.data || [];
      setCustomers(customersData);
      setStats(statsRes.data);
      setLoadError(null);
    } catch (error) {
      console.error('CRM load error:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Unknown error';
      setLoadError(errorMessage);
      toast.error(`Failed to load CRM data: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  // Pull-to-refresh handler
  const handleRefresh = useCallback(async () => {
    try {
      const params = { sort_by: sortBy };
      if (stageFilter !== 'all') params.stage = stageFilter;
      if (activityFilter !== 'all') params.last_activity_days = parseInt(activityFilter);
      if (followupsDueToday) params.followups_due_today = 'true';
      
      // First Visit Date Filter
      if (firstVisitFilter !== 'any') {
        if (firstVisitFilter === 'custom') {
          if (firstVisitCustomFrom) params.first_visit_from = firstVisitCustomFrom;
          if (firstVisitCustomTo) params.first_visit_to = firstVisitCustomTo;
        } else {
          params.first_visit_preset = firstVisitFilter;
        }
      }
      
      // Scheduled Visit Filter
      if (scheduledVisitFilter !== 'any') {
        if (scheduledVisitFilter === 'custom') {
          params.scheduled_visit_preset = 'custom';
          if (scheduledVisitCustomFrom) params.scheduled_visit_from = scheduledVisitCustomFrom;
          if (scheduledVisitCustomTo) params.scheduled_visit_to = scheduledVisitCustomTo;
        } else {
          params.scheduled_visit_preset = scheduledVisitFilter;
        }
        params.sort_by = 'scheduled_visit';
      }
      
      // Retargeting Filter
      if (retargetingFilter) {
        params.is_retargeting = 'true';
      }
      
      if (isAdmin) {
        params.view_all = 'true';
        if (cityFilter && cityFilter !== 'all') params.city = cityFilter;
        if (caFilter === 'unassigned') params.unassigned = 'true';
        else if (caFilter && caFilter !== 'all') params.rep_user_id = caFilter;
      }
      
      // Build stats params - SAME filters for consistency
      const statsParams = { ...params };
      delete statsParams.sort_by;
      delete statsParams.stage;
      if (debouncedSearchQuery) {
        statsParams.search = debouncedSearchQuery;
        params.search = debouncedSearchQuery;
      }
      
      const [customersRes, statsRes] = await Promise.all([
        api.getCustomers(params),
        api.getCRMStats(statsParams)
      ]);
      const customersData = customersRes.data?.customers || customersRes.data || [];
      setCustomers(customersData);
      setStats(statsRes.data);
      toast.success('CRM refreshed');
    } catch (error) {
      toast.error('Refresh failed');
    }
  }, [stageFilter, activityFilter, sortBy, followupsDueToday, caFilter, cityFilter, firstVisitFilter, firstVisitCustomFrom, firstVisitCustomTo, scheduledVisitFilter, scheduledVisitCustomFrom, scheduledVisitCustomTo, retargetingFilter, debouncedSearchQuery, isAdmin]);

  // Get urgency display for follow-up
  const getFollowupUrgency = (isoString) => {
    if (!isoString) return null;
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = date - now;
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      const overdueDays = Math.abs(diffDays);
      return {
        text: `${overdueDays} day${overdueDays > 1 ? 's' : ''} overdue`,
        color: 'text-[#5B3DF5]',
        bgColor: 'bg-red-50',
        icon: '🔴',
        isOverdue: true
      };
    } else if (diffDays === 0) {
      return {
        text: 'Due today',
        color: 'text-amber-600',
        bgColor: 'bg-amber-50',
        icon: '🟡',
        isOverdue: false
      };
    } else if (diffDays === 1) {
      return {
        text: 'Due tomorrow',
        color: 'text-emerald-600',
        bgColor: 'bg-emerald-50',
        icon: '🟢',
        isOverdue: false
      };
    } else {
      return {
        text: `Due in ${diffDays} days`,
        color: 'text-slate-500',
        bgColor: 'bg-slate-50',
        icon: '🟢',
        isOverdue: false
      };
    }
  };

  // Get conversion risk status
  const getConversionRisk = (customer) => {
    const now = new Date();
    const lastActivity = customer.last_activity_at ? new Date(customer.last_activity_at) : null;
    const daysSinceActivity = lastActivity ? Math.floor((now - lastActivity) / (1000 * 60 * 60 * 24)) : 999;
    
    // Risk conditions
    const noFollowupScheduled = !customer.next_followup_datetime;
    const noActivityOver3Days = daysSinceActivity > 3;
    const hotWithNoRevisit = customer.stage === 'Hot' && (customer.revisit_count || 0) === 0;
    const manyRevisitsNoBooking = (customer.revisit_count || 0) >= 3 && customer.stage !== 'Won';
    
    if (noActivityOver3Days && noFollowupScheduled) return true;
    if (hotWithNoRevisit && daysSinceActivity > 2) return true;
    if (manyRevisitsNoBooking) return true;
    
    return false;
  };

  // Get lead funnel stage
  const getFunnelStage = (customer) => {
    if (customer.stage === 'Won') return 4; // Booking
    if ((customer.revisit_count || 0) > 0) return 3; // Revisit
    if ((customer.total_visit_events || 0) > 0) return 2; // Visit
    return 1; // Lead
  };

  const getStageBadgeClass = (stage) => {
    const config = {
      Hot: 'bg-[#5B3DF5] text-white border-red-600',
      Warm: 'bg-amber-500 text-white border-amber-600',
      Cold: 'bg-slate-400 text-white border-slate-500',
      Won: 'bg-emerald-500 text-white border-emerald-600',
      Lost: 'bg-slate-200 text-slate-500 border-slate-300',
    };
    return config[stage] || 'bg-slate-100 text-slate-700';
  };

  const formatBudget = (minLacs, maxLacs) => {
    if (!minLacs && !maxLacs) return '';
    const min = minLacs ? `₹${minLacs}L` : '';
    const max = maxLacs ? `₹${maxLacs}L` : '';
    if (min && max) return `${min} - ${max}`;
    return min || max;
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return '';
    try {
      const date = new Date(isoString);
      if (isNaN(date.getTime())) return ''; // Return empty string for invalid dates
      const now = new Date();
      const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
      
      if (diffDays === 0) return `Today`;
      if (diffDays === 1) return `Yesterday`;
      if (diffDays < 7) return `${diffDays}d ago`;
      return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
    } catch {
      return '';
    }
  };

  // Since search is now handled server-side, just use customers directly
  // (keeping filteredCustomers variable name for compatibility)
  const filteredCustomers = customers;

  // Stage summary chips data
  const stageChips = useMemo(() => [
    { key: 'all', label: 'All', count: stats?.total_customers || 0 },
    { key: 'Hot', label: 'Hot', count: stats?.hot || 0, icon: Flame, activeColor: 'bg-[#5B3DF5] text-white' },
    { key: 'Warm', label: 'Warm', count: stats?.warm || 0, icon: ThermometerSun, activeColor: 'bg-amber-500 text-white' },
    { key: 'Cold', label: 'Cold', count: stats?.cold || 0, icon: Snowflake, activeColor: 'bg-slate-500 text-white' },
    { key: 'Won', label: 'Won', count: stats?.won || 0, icon: CheckCircle, activeColor: 'bg-emerald-500 text-white' },
    { key: 'Lost', label: 'Lost', count: stats?.lost || 0, icon: XCircle, activeColor: 'bg-slate-400 text-white' },
  ], [stats]);

  // Swipe handlers
  const handleTouchStart = (e, customerId) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e, customerId) => {
    const touchEndX = e.changedTouches[0].clientX;
    const diff = touchStartX.current - touchEndX;
    if (diff > 80) {
      setSwipedCardId(customerId);
    } else if (diff < -50) {
      setSwipedCardId(null);
    }
  };

  const handleAddCustomer = () => {
    setAddingCustomer(true);
    navigate('/crm/add');
  };

  // Bulk Upload Handlers (Admin only)
  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.name.endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (event) => {
      setCsvContent(event.target.result);
      toast.success(`File loaded: ${file.name}`);
    };
    reader.onerror = () => {
      toast.error('Failed to read file');
    };
    reader.readAsText(file);
  };

  const handleBulkUpload = async () => {
    if (!csvContent.trim()) {
      toast.error('Please upload or paste CSV content');
      return;
    }

    setUploadLoading(true);
    setUploadResults(null);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/crm/bulk-upload`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          credentials: 'include',
          body: JSON.stringify({ csv_content: csvContent })
        }
      );

      const data = await response.json();
      
      if (!response.ok) {
        toast.error(data.detail || 'Upload failed');
        return;
      }

      setUploadResults(data);
      
      if (data.created > 0) {
        toast.success(`Success: ${data.created} customers created`);
        fetchData(); // Refresh the customer list
      }
    } catch (error) {
      toast.error('Upload failed');
    } finally {
      setUploadLoading(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/crm/bulk-upload/template`,
        {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
          credentials: 'include'
        }
      );
      
      if (!response.ok) throw new Error('Failed to download');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'customer_upload_template.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Template downloaded');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const resetUpload = () => {
    setCsvContent('');
    setUploadResults(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="h-[100dvh] bg-[#F7F8FA] page-fade-in">
      {/* Header - Standardized ← Title format */}
      <div className="bg-white border-b border-slate-200 px-4 py-3 shadow-sm header-blur sticky top-0 z-10">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            {/* Back Button - Always visible */}
            <button
              onClick={() => navigate(-1)}
              className="p-1.5 -ml-1.5 rounded-lg hover:bg-slate-100 transition-colors"
              data-testid="page-back-btn"
            >
              <ArrowLeft size={20} className="text-slate-700" strokeWidth={2.5} />
            </button>
            <div>
              <h1 className="text-lg font-bold text-[#111827]">CRM</h1>
              <p className="text-xs text-slate-400 hidden sm:block">Site Visit Tracker</p>
            </div>
          </div>
          
          {/* Right side: Admin controls + Hamburger */}
          <div className="flex items-center gap-2">
            {/* Admin Header Controls */}
            {isAdmin && (
              <div className="hidden md:flex items-center gap-3">
                {/* CA Filter - Prominent placement */}
                <Select value={caFilter} onValueChange={setCaFilter}>
                  <SelectTrigger className="h-9 w-48 rounded-lg text-sm border-slate-200" data-testid="ca-filter-header">
                    <Users size={14} className="mr-2 text-slate-400" />
                    <SelectValue placeholder="All CAs" />
                  </SelectTrigger>
                  <SelectContent className="max-h-64">
                    <SelectItem value="all">All CAs</SelectItem>
                    <SelectItem value="unassigned">
                      <span className="flex items-center gap-2 text-red-600">
                        <XCircle size={14} />
                        Unassigned Only
                      </span>
                    </SelectItem>
                    {caList.map(ca => (
                      <SelectItem key={ca.user_id} value={ca.user_id}>
                        <span className="flex items-center gap-2">
                          <User size={14} className="text-slate-400" />
                          {ca.name}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Button
                  data-testid="bulk-upload-button"
                  variant="outline"
                  size="sm"
                  onClick={() => { setShowBulkUpload(true); resetUpload(); }}
                  className="h-9 rounded-lg"
                >
                  <Upload size={16} className="mr-1" />
                  Bulk Upload
                </Button>
              </div>
            )}
            
            {/* Hamburger Menu - Mobile */}
            <MobileMenuTrigger user={user} />
          </div>
        </div>
      </div>

      {/* Action Bar */}
      <div className="sticky top-0 z-20 bg-[#F7F8FA] px-4 py-3 shadow-sm">
        <div className="flex gap-2 max-w-7xl mx-auto">
          <Button
            data-testid="add-customer-button"
            onClick={handleAddCustomer}
            disabled={addingCustomer}
            className="bg-[#5B3DF5] hover:bg-[#4A2FE0] h-10 px-6 rounded-lg font-bold shadow-md"
          >
            {addingCustomer ? (
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
            ) : (
              <>
                <Plus size={18} className="mr-1" />
                Add Customer
              </>
            )}
          </Button>
          
          {/* Search - Centered */}
          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                data-testid="search-input"
                placeholder="Search customers..."
                className="pl-9 h-10 rounded-lg border-slate-200 bg-white"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
          
          {/* Filters Toggle */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
            className={`h-10 px-3 rounded-lg ${showFilters ? 'bg-slate-100' : ''}`}
          >
            <SlidersHorizontal size={16} />
          </Button>
        </div>
      </div>

      <PullToRefresh onRefresh={handleRefresh}>
        <div className="p-4 space-y-3 max-w-7xl mx-auto pb-20">
        {/* Filter Chips - Stage filters */}
        <div className="flex gap-2 overflow-x-auto pb-1 hide-scrollbar">
          {stageChips.map((chip) => {
            const Icon = chip.icon;
            const isActive = stageFilter === chip.key && !followupsDueToday;
            return (
              <button
                key={chip.key}
                data-testid={`filter-${chip.key.toLowerCase()}`}
                onClick={() => { setStageFilter(chip.key); setFollowupsDueToday(false); }}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all duration-200
                  ${isActive 
                    ? (chip.activeColor || 'bg-[#111827] text-white') + ' shadow-sm' 
                    : 'bg-white text-slate-500 border border-slate-200 hover:border-slate-300'}`}
              >
                {Icon && <Icon size={14} />}
                <span>{chip.label}</span>
                <span className={`ml-0.5 text-[11px] font-bold
                  ${isActive ? 'opacity-70' : 'text-slate-400'}`}>
                  {chip.count}
                </span>
              </button>
            );
          })}
          {/* Follow-ups Due Today chip */}
          <button
            data-testid="filter-followups-today"
            onClick={() => { setFollowupsDueToday(true); setStageFilter('all'); }}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all duration-200
              ${followupsDueToday 
                ? 'bg-purple-500 text-white shadow-sm' 
                : 'bg-white text-slate-500 border border-slate-200 hover:border-slate-300'}`}
          >
            <Bell size={14} />
            <span>Due Today</span>
            <span className={`ml-0.5 text-[11px] font-bold ${followupsDueToday ? 'opacity-70' : 'text-slate-400'}`}>
              {stats?.followups_due_today || 0}
            </span>
          </button>
          
          {/* Visits Today chip */}
          <button
            data-testid="filter-visits-today"
            onClick={() => { 
              setScheduledVisitFilter('today');
              setStageFilter('all'); 
              setFollowupsDueToday(false);
            }}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all duration-200
              ${scheduledVisitFilter === 'today' 
                ? 'bg-emerald-500 text-white shadow-sm' 
                : 'bg-white text-slate-500 border border-slate-200 hover:border-slate-300'}`}
          >
            <Calendar size={14} />
            <span>Visits Today</span>
            <span className={`ml-0.5 text-[11px] font-bold ${scheduledVisitFilter === 'today' ? 'opacity-70' : 'text-slate-400'}`}>
              {stats?.visits_today || 0}
            </span>
          </button>
          
          {/* Retargeting Leads chip */}
          <button
            data-testid="filter-retargeting"
            onClick={() => { 
              setRetargetingFilter(!retargetingFilter);
              if (!retargetingFilter) {
                setStageFilter('all'); 
                setFollowupsDueToday(false);
                setScheduledVisitFilter('any');
              }
            }}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all duration-200
              ${retargetingFilter 
                ? 'bg-orange-500 text-white shadow-sm' 
                : 'bg-white text-slate-500 border border-slate-200 hover:border-slate-300'}`}
          >
            <RotateCcw size={14} />
            <span>Retargeting</span>
            <span className={`ml-0.5 text-[11px] font-bold ${retargetingFilter ? 'opacity-70' : 'text-slate-400'}`}>
              {stats?.retargeting_leads || 0}
            </span>
          </button>
        </div>

        {/* Advanced Filters - Compact inline */}
        {showFilters && (
          <Card className="p-3 bg-white shadow-sm rounded-xl">
            <div className="flex items-center gap-4 flex-wrap">
              {/* City Filter - Admin/Manager only */}
              {isAdmin && (
                <div className="flex items-center gap-2">
                  <label className="text-xs text-slate-500 font-medium">City:</label>
                  <Select value={cityFilter} onValueChange={setCityFilter}>
                    <SelectTrigger className="h-8 w-32 rounded-lg text-sm" data-testid="city-filter">
                      <MapPin size={12} className="mr-1 text-slate-400" />
                      <SelectValue placeholder="All Cities" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Cities</SelectItem>
                      {cityList.map(city => (
                        <SelectItem key={city} value={city}>{city}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              {/* First Visit Date Filter */}
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1">
                  <label className="text-xs text-slate-500 font-medium">First Visit Date:</label>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info size={12} className="text-slate-400 cursor-help hover:text-slate-600 transition-colors" />
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className="max-w-[250px] text-center">
                        <p className="text-xs">Filters customers by the date of their first project visit. Use this to analyze visit inflow trends.</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <Popover open={showCustomDatePicker && firstVisitFilter === 'custom'} onOpenChange={setShowCustomDatePicker}>
                  <PopoverTrigger asChild>
                    <div>
                      <Select 
                        value={firstVisitFilter} 
                        onValueChange={(val) => {
                          setFirstVisitFilter(val);
                          if (val === 'custom') {
                            setShowCustomDatePicker(true);
                          } else {
                            setShowCustomDatePicker(false);
                            setFirstVisitCustomFrom('');
                            setFirstVisitCustomTo('');
                          }
                        }}
                      >
                        <SelectTrigger className="h-8 w-36 rounded-lg text-sm" data-testid="first-visit-filter">
                          <CalendarDays size={12} className="mr-1 text-slate-400" />
                          <SelectValue placeholder="Any time" />
                        </SelectTrigger>
                        <SelectContent>
                          {FIRST_VISIT_PRESETS.map(preset => (
                            <SelectItem key={preset.value} value={preset.value}>{preset.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </PopoverTrigger>
                  <PopoverContent className="w-64 p-3" align="start">
                    <div className="space-y-3">
                      <p className="text-xs font-medium text-slate-600">Select date range</p>
                      <div className="space-y-2">
                        <div>
                          <Label className="text-xs text-slate-500">From</Label>
                          <Input
                            type="date"
                            value={firstVisitCustomFrom}
                            onChange={(e) => setFirstVisitCustomFrom(e.target.value)}
                            className="h-8 text-sm"
                            data-testid="first-visit-from"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-slate-500">To</Label>
                          <Input
                            type="date"
                            value={firstVisitCustomTo}
                            onChange={(e) => setFirstVisitCustomTo(e.target.value)}
                            className="h-8 text-sm"
                            data-testid="first-visit-to"
                          />
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => {
                            if (firstVisitCustomFrom && firstVisitCustomTo) {
                              setShowCustomDatePicker(false);
                              fetchData();
                            } else {
                              toast.error('Please select both dates');
                            }
                          }}
                          className="flex-1 h-7 text-xs"
                          data-testid="apply-first-visit-range"
                        >
                          Apply
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setFirstVisitFilter('any');
                            setFirstVisitCustomFrom('');
                            setFirstVisitCustomTo('');
                            setShowCustomDatePicker(false);
                          }}
                          className="h-7 text-xs"
                        >
                          Clear
                        </Button>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
              
              {/* Scheduled Visit Filter */}
              <div className="flex items-center gap-2">
                <label className="text-xs text-slate-500 font-medium">Scheduled:</label>
                <Popover open={showScheduledDatePicker && scheduledVisitFilter === 'custom'} onOpenChange={setShowScheduledDatePicker}>
                  <PopoverTrigger asChild>
                    <div>
                      <Select 
                        value={scheduledVisitFilter} 
                        onValueChange={(val) => {
                          setScheduledVisitFilter(val);
                          if (val === 'custom') {
                            setShowScheduledDatePicker(true);
                          } else {
                            setShowScheduledDatePicker(false);
                            setScheduledVisitCustomFrom('');
                            setScheduledVisitCustomTo('');
                          }
                        }}
                      >
                        <SelectTrigger className="h-8 w-32 rounded-lg text-sm" data-testid="scheduled-visit-filter">
                          <Calendar size={12} className="mr-1 text-slate-400" />
                          <SelectValue placeholder="Any" />
                        </SelectTrigger>
                        <SelectContent>
                          {SCHEDULED_VISIT_PRESETS.map(preset => (
                            <SelectItem key={preset.value} value={preset.value}>{preset.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </PopoverTrigger>
                  <PopoverContent className="w-64 p-3" align="start">
                    <div className="space-y-3">
                      <p className="text-xs font-medium text-slate-600">Select date range for scheduled visits</p>
                      <div className="space-y-2">
                        <div>
                          <Label className="text-xs text-slate-500">From</Label>
                          <Input
                            type="date"
                            value={scheduledVisitCustomFrom}
                            onChange={(e) => setScheduledVisitCustomFrom(e.target.value)}
                            className="h-8 text-sm"
                            data-testid="scheduled-visit-from"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-slate-500">To</Label>
                          <Input
                            type="date"
                            value={scheduledVisitCustomTo}
                            onChange={(e) => setScheduledVisitCustomTo(e.target.value)}
                            className="h-8 text-sm"
                            data-testid="scheduled-visit-to"
                          />
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => {
                            if (scheduledVisitCustomFrom && scheduledVisitCustomTo) {
                              setShowScheduledDatePicker(false);
                              fetchData();
                            } else {
                              toast.error('Please select both dates');
                            }
                          }}
                          className="flex-1 h-7 text-xs"
                          data-testid="apply-scheduled-visit-range"
                        >
                          Apply
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setScheduledVisitFilter('any');
                            setScheduledVisitCustomFrom('');
                            setScheduledVisitCustomTo('');
                            setShowScheduledDatePicker(false);
                          }}
                          className="h-7 text-xs"
                        >
                          Clear
                        </Button>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
              
              <div className="flex items-center gap-2">
                <label className="text-xs text-slate-500 font-medium">Activity:</label>
                <Select value={activityFilter} onValueChange={setActivityFilter}>
                  <SelectTrigger className="h-8 w-32 rounded-lg text-sm">
                    <SelectValue placeholder="Any time" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Any time</SelectItem>
                    <SelectItem value="7">Last 7 days</SelectItem>
                    <SelectItem value="14">Last 14 days</SelectItem>
                    <SelectItem value="30">Last 30 days</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <label className="text-xs text-slate-500 font-medium">Sort:</label>
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="h-8 w-40 rounded-lg text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="next_followup">Next Follow-up</SelectItem>
                    <SelectItem value="last_activity">Last Activity</SelectItem>
                    <SelectItem value="first_visit_newest">First Visit (Newest)</SelectItem>
                    <SelectItem value="first_visit_oldest">First Visit (Oldest)</SelectItem>
                    <SelectItem value="scheduled_visit">Scheduled Visit</SelectItem>
                    <SelectItem value="most_visits">Most Visits</SelectItem>
                    {isAdmin && <SelectItem value="assigned_ca">Assigned CA</SelectItem>}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Active Filter Helper Text */}
            {firstVisitFilter !== 'any' && (
              <div className="mt-2 pt-2 border-t border-slate-100">
                <p className="text-xs text-purple-600 flex items-center gap-1">
                  <CalendarDays size={12} />
                  <span>
                    First Visit Date: {
                      firstVisitFilter === 'custom' && firstVisitCustomFrom && firstVisitCustomTo
                        ? `${new Date(firstVisitCustomFrom).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} – ${new Date(firstVisitCustomTo).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}`
                        : FIRST_VISIT_PRESETS.find(p => p.value === firstVisitFilter)?.label || firstVisitFilter
                    }
                  </span>
                  <button 
                    onClick={() => {
                      setFirstVisitFilter('any');
                      setFirstVisitCustomFrom('');
                      setFirstVisitCustomTo('');
                    }}
                    className="ml-2 text-slate-400 hover:text-slate-600"
                  >
                    ✕
                  </button>
                </p>
              </div>
            )}
            
            {/* Scheduled Visit Filter Indicator */}
            {scheduledVisitFilter !== 'any' && (
              <div className={`mt-2 pt-2 ${firstVisitFilter === 'any' ? 'border-t border-slate-100' : ''}`}>
                <p className="text-xs text-emerald-600 flex items-center gap-1">
                  <Calendar size={12} />
                  <span>
                    Scheduled Visits: {
                      scheduledVisitFilter === 'custom' && scheduledVisitCustomFrom && scheduledVisitCustomTo
                        ? `${new Date(scheduledVisitCustomFrom).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} – ${new Date(scheduledVisitCustomTo).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}`
                        : SCHEDULED_VISIT_PRESETS.find(p => p.value === scheduledVisitFilter)?.label || scheduledVisitFilter
                    }
                  </span>
                  <button 
                    onClick={() => {
                      setScheduledVisitFilter('any');
                      setScheduledVisitCustomFrom('');
                      setScheduledVisitCustomTo('');
                    }}
                    className="ml-2 text-slate-400 hover:text-slate-600"
                  >
                    ✕
                  </button>
                </p>
              </div>
            )}
          </Card>
        )}

        {/* Customer List */}
        {loading ? (
          <CRMListSkeleton />
        ) : loadError ? (
          <Card className="p-12 text-center bg-white shadow-sm rounded-2xl">
            <AlertTriangle size={48} className="mx-auto mb-4 text-red-400" />
            <p className="text-slate-800 font-semibold mb-2">Failed to Load Customers</p>
            <p className="text-slate-500 text-sm mb-4">{loadError}</p>
            <Button onClick={() => fetchCustomers()} className="bg-[#5B3DF5] hover:bg-[#4A2FE0]">
              <RefreshCw size={16} className="mr-2" /> Retry
            </Button>
          </Card>
        ) : filteredCustomers.length === 0 ? (
          <Card className="p-12 text-center bg-white shadow-sm rounded-2xl">
            <Building2 size={48} className="mx-auto mb-4 text-slate-300" />
            <p className="text-slate-600 font-semibold mb-2">
              {searchQuery || followupsDueToday ? 'No customers found' : 'No customers yet'}
            </p>
            <p className="text-slate-400 text-sm mb-4">
              Add your first customer after completing a site visit
            </p>
            <Button onClick={() => navigate('/crm/add')} className="bg-[#5B3DF5] hover:bg-[#4A2FE0]">
              Add First Customer
            </Button>
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-3">
            {filteredCustomers.map((customer) => {
              const followupUrgency = getFollowupUrgency(customer.next_followup_datetime);
              
              // Calculate days since last activity for risk border
              const daysSinceActivity = customer.last_activity_at 
                ? Math.floor((Date.now() - new Date(customer.last_activity_at).getTime()) / (1000 * 60 * 60 * 24))
                : 999;
              
              // Check if customer has any visits
              const hasVisits = customer.total_visit_events > 0 || customer.first_visit_date;
              
              // Risk border color based on activity:
              // 🟢 Green → < 5 days | 🟡 Amber → 5-9 days | 🔴 Red → 10+ days | ⚫ Grey → No visit yet
              const getRiskBorder = () => {
                if (!hasVisits) return 'border-l-slate-300'; // Grey - no visit yet
                if (daysSinceActivity < 5) return 'border-l-emerald-500'; // Green - active
                if (daysSinceActivity < 10) return 'border-l-amber-400'; // Amber - attention
                return 'border-l-red-500'; // Red - VDNB risk
              };
              const riskBorderColor = getRiskBorder();
              
              // Heat tag styling - prioritize terminal outcomes (Won/Lost) over warmth
              // This ensures cards show correct badge in Won/Lost filtered views
              const getHeatTag = (stage) => {
                // Priority 1: Terminal outcomes take precedence
                if (stage === 'Won') return { label: 'WON', bg: 'bg-emerald-500', text: 'text-white' };
                if (stage === 'Lost') return { label: 'LOST', bg: 'bg-slate-400', text: 'text-white' };
                if (stage === 'PENDING_LOST_REVIEW') return { label: 'REVIEW', bg: 'bg-amber-500', text: 'text-white' };
                // Priority 2: Active pipeline stages
                if (stage === 'Hot') return { label: 'HOT', bg: 'bg-red-500', text: 'text-white' };
                if (stage === 'Warm') return { label: 'WARM', bg: 'bg-amber-400', text: 'text-amber-900' };
                // Default: Cold for unknown/legacy stages
                return { label: 'COLD', bg: 'bg-slate-200', text: 'text-slate-600' };
              };
              const heatTag = getHeatTag(customer.stage);
              
              // Format dates compactly
              const formatShortDate = (dateStr) => {
                if (!dateStr) return '—';
                try {
                  return new Date(dateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
                } catch { return '—'; }
              };
              
              // Better activity display
              const getActivityText = () => {
                if (!customer.last_activity_at) return 'No activity';
                if (daysSinceActivity === 0) return 'Today';
                if (daysSinceActivity === 1) return 'Yesterday';
                return `${daysSinceActivity}d ago`;
              };
              
              // Get follow-up status with stronger visual for missing
              const getFollowupDisplay = () => {
                if (!customer.next_followup_datetime) {
                  return { text: '⚠ NO FOLLOW-UP', class: 'text-red-700 bg-red-100 font-bold', urgent: true };
                }
                const followupDate = new Date(customer.next_followup_datetime);
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const tomorrow = new Date(today);
                tomorrow.setDate(tomorrow.getDate() + 1);
                
                if (followupDate < today) {
                  return { text: '🔴 OVERDUE', class: 'text-red-700 bg-red-100 font-bold', urgent: true };
                }
                if (followupDate < tomorrow) {
                  return { text: '● Today', class: 'text-emerald-700 bg-emerald-50 font-semibold', urgent: false };
                }
                return { text: formatShortDate(customer.next_followup_datetime), class: 'text-slate-600 bg-slate-100', urgent: false };
              };
              const followupDisplay = getFollowupDisplay();
              
              // Format budget in Cr
              const formatBudget = () => {
                const min = customer.budget_min_lacs;
                const max = customer.budget_max_lacs;
                if (!min && !max) return null;
                // Convert lacs to Cr for display
                const minCr = min ? (min / 100).toFixed(1) : null;
                const maxCr = max ? (max / 100).toFixed(1) : null;
                if (minCr && maxCr) return `₹${minCr}–${maxCr} Cr`;
                if (minCr) return `₹${minCr} Cr+`;
                return `Up to ₹${maxCr} Cr`;
              };
              
              // Check if has visit scheduled today
              const hasVisitToday = customer.has_visit_today || false;
              const scheduledVisitTime = customer.scheduled_visit_datetime;
              
              // Format scheduled visit time
              const formatScheduledTime = (dt) => {
                if (!dt) return null;
                try {
                  const date = new Date(dt);
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  const tomorrow = new Date(today);
                  tomorrow.setDate(tomorrow.getDate() + 1);
                  const visitDate = new Date(dt);
                  visitDate.setHours(0, 0, 0, 0);
                  
                  const time = new Date(dt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
                  if (visitDate.getTime() === today.getTime()) return `Today ${time}`;
                  if (visitDate.getTime() === tomorrow.getTime()) return `Tomorrow ${time}`;
                  return new Date(dt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) + ` ${time}`;
                } catch { return null; }
              };
              
              return (
                <Card 
                  key={customer.customer_id}
                  data-testid={`customer-${customer.customer_id}`}
                  className={`bg-white shadow-sm rounded-lg border-l-[5px] ${riskBorderColor} hover:shadow-md transition-all duration-150 cursor-pointer ${hasVisitToday ? 'ring-2 ring-emerald-400 ring-offset-1' : ''}`}
                  onClick={() => navigate(`/crm/${customer.customer_id}`)}
                >
                  {/* ROW 1 - Name + Heat | Activity + Follow-up */}
                  <div className="px-2.5 pt-2 pb-1 flex items-center justify-between">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <h3 className="font-bold text-[#111827] truncate text-[13px]">
                        {customer.customer_name}
                      </h3>
                      <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${heatTag.bg} ${heatTag.text}`}>
                        {heatTag.label}
                      </span>
                      {/* Visit Today Indicator */}
                      {hasVisitToday && (
                        <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-emerald-500 text-white flex items-center gap-0.5">
                          <Calendar size={9} />
                          VISIT
                        </span>
                      )}
                      {/* Retargeting Lead Tag - differentiate between Lost Lead Recovery and VDNB */}
                      {customer.is_retargeting_lead && (
                        <TooltipProvider delayDuration={100}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold flex items-center gap-0.5 cursor-help border ${
                                customer.retargeting_type === 'VDNB' 
                                  ? 'bg-purple-100 text-purple-700 border-purple-200' 
                                  : 'bg-orange-100 text-orange-700 border-orange-200'
                              }`}>
                                <RotateCcw size={9} />
                                {customer.retargeting_type === 'VDNB' ? 'VDNB' : 'RETARGETING'}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent side="bottom" className="bg-slate-800 text-white p-2 max-w-[200px]">
                              <div className="text-[10px] space-y-1">
                                <p className={`font-semibold ${customer.retargeting_type === 'VDNB' ? 'text-purple-300' : 'text-orange-300'}`}>
                                  {customer.retargeting_type === 'VDNB' ? 'VDNB Recovery' : 'Lost Lead Recovery'}
                                </p>
                                {customer.original_rep_name && (
                                  <p>Original Rep: <span className="font-medium">{customer.original_rep_name}</span></p>
                                )}
                                {(customer.reassigned_from_lost_by_name || customer.reassigned_from_vdnb_by_name) && (
                                  <p>Reassigned By: <span className="font-medium">{customer.reassigned_from_lost_by_name || customer.reassigned_from_vdnb_by_name}</span></p>
                                )}
                                {(customer.reassigned_from_lost_at || customer.reassigned_from_vdnb_at) && (
                                  <p>Date: <span className="font-medium">{new Date(customer.reassigned_from_lost_at || customer.reassigned_from_vdnb_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span></p>
                                )}
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-[10px] text-slate-400">{getActivityText()}</span>
                      <span className={`text-[9px] px-1.5 py-0.5 rounded ${followupDisplay.class}`}>
                        {followupDisplay.text}
                      </span>
                    </div>
                  </div>
                  
                  {/* ROW 2 - CA | First Visit Date | Last Visit | Scheduled Visit */}
                  <div className="px-2.5 py-1 flex items-center gap-3 text-[10px] text-slate-500 border-t border-slate-50">
                    <button 
                      className="flex items-center gap-1 hover:text-[#5B3DF5] transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (customer.rep_user_id || customer.rep_id) {
                          const ca = caList.find(c => c.name === customer.rep_name);
                          if (ca) setCaFilter(ca.user_id);
                        }
                      }}
                    >
                      <User size={10} className="text-slate-400" />
                      <span className={`font-medium ${!(customer.rep_user_id || customer.rep_id) ? 'text-red-500' : 'text-slate-600'}`}>
                        {customer.rep_name || customer.rep_id || 'Unassigned'}
                      </span>
                    </button>
                    <span className="text-slate-200">|</span>
                    {/* Highlight First Visit Date when filter is active */}
                    <span className={firstVisitFilter !== 'any' ? 'bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded font-medium' : ''}>
                      <span className="text-slate-400">1st Visit:</span> {formatShortDate(customer.first_visit_date)}
                    </span>
                    <span><span className="text-slate-400">Last:</span> {formatShortDate(customer.last_activity_at)}</span>
                    {/* Scheduled Visit - highlight when filter is active or visit is today */}
                    {scheduledVisitTime && (
                      <>
                        <span className="text-slate-200">|</span>
                        <span className={`flex items-center gap-1 ${hasVisitToday || scheduledVisitFilter !== 'any' ? 'bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded font-medium' : ''}`}>
                          <Calendar size={10} className="text-emerald-500" />
                          <span className="text-slate-400">Next:</span> {formatScheduledTime(scheduledVisitTime)}
                        </span>
                      </>
                    )}
                  </div>
                  
                  {/* ROW 3 - Locality + Budget Pills */}
                  <div className="px-2.5 py-1.5 flex items-center gap-1.5 flex-wrap">
                    {customer.locality && (
                      <span className="text-[9px] px-2 py-0.5 bg-blue-50 text-blue-600 rounded-full flex items-center gap-1">
                        <Building2 size={9} />
                        {customer.locality}
                      </span>
                    )}
                    {formatBudget() && (
                      <span className="text-[9px] px-2 py-0.5 bg-emerald-50 text-emerald-600 rounded-full">
                        {formatBudget()}
                      </span>
                    )}
                    {!customer.locality && !formatBudget() && (
                      <span className="text-[9px] text-slate-300 italic">No locality/budget</span>
                    )}
                  </div>
                  
                  {/* ROW 4 - KPI Row (Visits + Projects) */}
                  <div className="px-2.5 pb-2 pt-0.5 flex items-center gap-3 text-[9px] text-slate-400">
                    <span className="flex items-center gap-1">
                      <Eye size={10} />
                      {customer.total_visit_events || 0} visits
                    </span>
                    <span className="flex items-center gap-1">
                      <Building2 size={10} />
                      {customer.unique_projects || 0} projects
                    </span>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
      </PullToRefresh>

      {/* Bulk Upload Dialog (Admin only) */}
      {isAdmin && (
        <Dialog open={showBulkUpload} onOpenChange={(open) => { setShowBulkUpload(open); if (!open) resetUpload(); }}>
          <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Upload size={20} />
                Bulk Upload Customers
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 pt-2">
              {/* Instructions */}
              <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
                <p className="font-medium mb-1">📋 CSV Format</p>
                <p className="text-xs">Required: customer_name, customer_email, rep_email, first_project_name, visit_date, visit_source</p>
              </div>

              {/* Download Template */}
              <Button variant="outline" onClick={downloadTemplate} className="w-full">
                <Download size={16} className="mr-2" />
                Download CSV Template
              </Button>

              {/* File Upload */}
              <div 
                className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center hover:border-slate-300 transition-colors cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileUpload}
                  className="hidden"
                />
                <FileText size={32} className="mx-auto text-slate-400 mb-2" />
                <p className="text-sm text-slate-600">Click to select CSV file</p>
              </div>

              {/* Or paste */}
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-slate-200"></div>
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="bg-white px-2 text-slate-500">or paste CSV</span>
                </div>
              </div>

              <textarea
                value={csvContent}
                onChange={(e) => setCsvContent(e.target.value)}
                placeholder="Paste CSV content here..."
                className="w-full h-28 p-3 border rounded-lg text-sm font-mono resize-none focus:outline-none focus:ring-2 focus:ring-[#EF4444]"
              />

              {csvContent && (
                <div className="flex items-center gap-2 text-sm text-emerald-600">
                  <CheckCircle size={16} />
                  CSV loaded ({csvContent.split('\n').length} lines)
                </div>
              )}

              {/* Visit Source Info */}
              <div className="p-3 bg-amber-50 rounded-lg text-xs text-amber-700">
                <p className="font-medium mb-1">Valid visit_source values:</p>
                <p>Inside Sales, Customer Referral, Employee Referral, External Database, Rep Self Generated</p>
                <p className="mt-1 text-amber-600">Note: If "External Database", provide visit_source_detail</p>
              </div>

              {/* Upload Results */}
              {uploadResults && (
                <div className="space-y-3 p-3 bg-slate-50 rounded-lg">
                  <p className="font-medium text-sm text-slate-700">Upload Summary</p>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="p-2 bg-emerald-50 rounded">
                      <p className="text-lg font-bold text-emerald-600">{uploadResults.created}</p>
                      <p className="text-xs text-emerald-700">Created</p>
                    </div>
                    <div className="p-2 bg-amber-50 rounded">
                      <p className="text-lg font-bold text-amber-600">{uploadResults.skipped}</p>
                      <p className="text-xs text-amber-700">Skipped</p>
                    </div>
                    <div className="p-2 bg-red-50 rounded">
                      <p className="text-lg font-bold text-[#5B3DF5]">{uploadResults.failed}</p>
                      <p className="text-xs text-red-700">Failed</p>
                    </div>
                  </div>
                  
                  {uploadResults.errors && uploadResults.errors.length > 0 && (
                    <div className="mt-2 max-h-32 overflow-y-auto bg-red-50 p-2 rounded">
                      <p className="text-xs font-medium text-[#5B3DF5] mb-1">Errors:</p>
                      {uploadResults.errors.slice(0, 10).map((err, i) => (
                        <p key={i} className="text-xs text-[#5B3DF5]">{err}</p>
                      ))}
                      {uploadResults.errors.length > 10 && (
                        <p className="text-xs text-slate-500">...and {uploadResults.errors.length - 10} more</p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2 pt-4 border-t mt-4">
              <Button 
                variant="outline" 
                onClick={() => setShowBulkUpload(false)}
                className="flex-1"
              >
                {uploadResults ? 'Done' : 'Cancel'}
              </Button>
              {!uploadResults && (
                <Button 
                  onClick={handleBulkUpload}
                  disabled={!csvContent.trim() || uploadLoading}
                  className="flex-1 bg-[#5B3DF5] hover:bg-[#4A2FE0]"
                >
                  {uploadLoading ? (
                    <>
                      <Loader2 size={16} className="mr-2 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload size={16} className="mr-2" />
                      Upload Customers
                    </>
                  )}
                </Button>
              )}
              {uploadResults && (
                <Button 
                  onClick={resetUpload}
                  className="flex-1 bg-[#5B3DF5] hover:bg-[#4A2FE0]"
                >
                  Upload More
                </Button>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}

      
      <style jsx>{`
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </div>
  );
};

export default CRM;
